import React, { useState } from "react";
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup } from "react-simple-maps";
import { ZoomIn, ZoomOut, Maximize2 } from "lucide-react";

const WORLD_TOPO_URL =
  "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

/** World map with a blue pin per event. Clicking a pin scrolls the page to
 * that event's card. Zoomable + pannable to inspect dense areas. */
const EventsMap = ({ events = [] }) => {
  const [position, setPosition] = useState({ coordinates: [0, 20], zoom: 1 });

  const pins = events.filter(
    (e) =>
      typeof e.lat === "number" &&
      typeof e.lng === "number" &&
      !Number.isNaN(e.lat) &&
      !Number.isNaN(e.lng)
  );

  const zoom = (factor) => {
    setPosition((p) => {
      const next = Math.max(1, Math.min(8, p.zoom * factor));
      return { ...p, zoom: next };
    });
  };
  const reset = () => setPosition({ coordinates: [0, 20], zoom: 1 });

  const jumpToCard = (id) => {
    const el = document.getElementById(`event-${id}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      el.classList.add("events-card-highlight");
      setTimeout(() => el.classList.remove("events-card-highlight"), 2400);
    }
    // Update URL hash so the link is shareable.
    if (window.history.replaceState) {
      window.history.replaceState(null, "", `#${id}`);
    }
  };

  return (
    <div className="events-map" data-testid="events-map">
      <div className="members-map-inner">
        <div className="map-zoom-controls" role="group" aria-label="Zoom controls">
          <button type="button" onClick={() => zoom(1.5)} aria-label="Zoom in" data-testid="events-map-zoom-in"><ZoomIn size={14} /></button>
          <button type="button" onClick={() => zoom(1 / 1.5)} aria-label="Zoom out" data-testid="events-map-zoom-out"><ZoomOut size={14} /></button>
          <button type="button" onClick={reset} aria-label="Reset view" data-testid="events-map-reset"><Maximize2 size={14} /></button>
        </div>
        <ComposableMap
          projection="geoEqualEarth"
          width={980}
          height={470}
          projectionConfig={{ scale: 175 }}
        >
          <ZoomableGroup
            zoom={position.zoom}
            center={position.coordinates}
            minZoom={1}
            maxZoom={8}
            onMoveEnd={setPosition}
          >
            <Geographies geography={WORLD_TOPO_URL}>
              {({ geographies }) =>
                geographies.map((geo) => (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill="#111"
                    stroke="rgba(255,255,255,0.14)"
                    strokeWidth={0.6}
                    style={{
                      default: { outline: "none" },
                      hover: { outline: "none" },
                      pressed: { outline: "none" },
                    }}
                  />
                ))
              }
            </Geographies>
            {pins.map((e) => (
              <Marker
                key={e.id}
                coordinates={[e.lng, e.lat]}
                onClick={() => jumpToCard(e.id)}
                data-testid={`events-pin-${e.id}`}
                style={{
                  default: { cursor: "pointer" },
                  hover: { cursor: "pointer" },
                  pressed: { cursor: "pointer" },
                }}
              >
                <title>{e.title}{e.location_display ? ` · ${e.location_display}` : ""}</title>
                <circle r={6 / Math.sqrt(position.zoom)} className="events-map-pin" />
              </Marker>
            ))}
          </ZoomableGroup>
        </ComposableMap>
      </div>
      <div className="members-map-caption">
        <span className="members-map-legend"><span className="events-map-legend-dot" /> upcoming event</span>
        <span className="members-map-count">
          {pins.length} event{pins.length === 1 ? "" : "s"} on the map
        </span>
      </div>
    </div>
  );
};

export default EventsMap;
